import { useState } from "react";
import { MapPin, Navigation, MapPinned } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { INFO } from "@/data/site";
import { useLang } from "@/i18n";

const MAP_QUERY = encodeURIComponent(`${INFO.address}`);
const MAP_EMBED = `https://maps.google.com/maps?q=${MAP_QUERY}&t=&z=16&ie=UTF8&iwloc=&output=embed`;
const MAP_LINK = `https://www.google.com/maps/dir/?api=1&destination=${MAP_QUERY}`;

export const LocationMap = () => {
  const { t } = useLang();
  const [mapLoaded, setMapLoaded] = useState(false);
  return (
  <section id="anfahrt" data-testid="location-map" className="bg-background border-t border-border">
    <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-24 md:py-32">
      <div className="grid lg:grid-cols-12 gap-10 md:gap-14">
        <Reveal className="lg:col-span-4">
          <div>
            <span className="text-xs uppercase tracking-[0.25em] text-muted-foreground">{t("location.eyebrow")}</span>
            <h2 className="font-serif font-light text-4xl md:text-5xl tracking-tight mt-4">
              {t("location.title")}
            </h2>
            <div className="mt-8 space-y-5 text-foreground/80">
              <div className="flex items-start gap-3">
                <MapPin strokeWidth={1.25} size={18} className="mt-1 text-primary shrink-0" />
                <p className="leading-relaxed">
                  {INFO.address.split(",")[0].trim()}<br />
                  {INFO.address.split(",")[1]?.trim()}
                </p>
              </div>
              <a
                href={MAP_LINK}
                target="_blank"
                rel="noreferrer"
                data-testid="map-directions-link"
                className="inline-flex items-center gap-2 border border-primary text-primary px-6 py-3 text-sm tracking-wide hover:bg-primary hover:text-primary-foreground transition-colors mt-2"
              >
                <Navigation strokeWidth={1.5} size={15} /> {t("location.route")}
              </a>
              <p className="text-xs text-muted-foreground leading-relaxed pt-4 border-t border-border max-w-xs">
                {t("location.note")}
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-8">
          <div className="relative aspect-[16/10] md:aspect-[16/9] w-full overflow-hidden bg-muted border border-border">
            {mapLoaded ? (
              <iframe
                title={`Karte – ${INFO.name}`}
                src={MAP_EMBED}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="absolute inset-0 w-full h-full"
                data-testid="google-map-iframe"
                style={{ border: 0, filter: "grayscale(0.15) contrast(0.95)" }}
                allowFullScreen
              />
            ) : (
              <button
                type="button"
                onClick={() => setMapLoaded(true)}
                data-testid="map-consent-btn"
                className="absolute inset-0 w-full h-full flex flex-col items-center justify-center gap-4 text-center px-6 group hover:bg-muted/70 transition-colors"
                style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent 0 14px, rgba(0,0,0,0.03) 14px 28px)" }}
              >
                <MapPinned strokeWidth={1.25} size={38} className="text-primary group-hover:scale-110 transition-transform" />
                <p className="font-serif text-xl md:text-2xl">{t("location.consent_title")}</p>
                <p className="text-xs md:text-sm text-muted-foreground leading-relaxed max-w-md">
                  {t("location.consent_body")}
                </p>
                <span className="inline-block border border-primary text-primary px-6 py-2.5 text-sm mt-2 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  {t("location.consent_cta")}
                </span>
              </button>
            )}
          </div>
        </Reveal>
      </div>
    </div>
  </section>
  );
};
